/*-----------------------------------------------------------------------------/
/ Live search
/
/ Queries Shopify's Predictive Search API as the shopper types and renders the
/ top results into the header popup and the sidebar drawer.
/
/ Shopify picks its search analyzer from the store's primary locale and offers
/ no per-request language option. Hungarian gets typo tolerance but no stemming,
/ so agglutinated forms ("babakocsiban", "babakocsihoz") never reach the stem on
/ their own. normalizeQuery() strips those suffixes, but only as a fallback:
/ the words the shopper actually typed are always tried first.
/-----------------------------------------------------------------------------*/

(function () {
  'use strict';

  var config = (window.theme && window.theme.search) || {};

  if (!config.enabled) {
    return;
  }

  var DEBOUNCE_MS = 250;
  var MIN_QUERY_LENGTH = 2;

  // Below this length a word is left alone: too short to carry a suffix.
  var MIN_WORD_LENGTH = 5;
  // A stripped stem shorter than this is discarded as over-stemming.
  var MIN_STEM_LENGTH = 4;
  // The assimilation pattern is distinctive enough to trust on shorter roots,
  // of which Hungarian has many: bot, kés, ház.
  var MIN_ASSIMILATED_STEM_LENGTH = 3;

  /* Hungarian suffixes
     ========================================================================== */

  // Case endings. Unambiguous enough to strip from any word in the query.
  var CASE_SUFFIXES = [
    'képpen', 'képen', 'ként',
    'nak', 'nek', 'val', 'vel', 'ról', 'ről', 'tól', 'től',
    'ból', 'ből', 'ban', 'ben', 'hoz', 'hez', 'höz',
    'nál', 'nél', 'ért', 'ig', 'ra', 're', 'ba', 'be',
    'on', 'en', 'ön', 'ul', 'ül'
  ];

  // Plural, possessive and accusative endings. Only applied to the final word,
  // where Shopify's partial matching recovers anything stripped too eagerly.
  var LOOSE_SUFFIXES = [
    'jaink', 'jeink', 'aink', 'eink',
    'unk', 'ünk', 'juk', 'jük',
    'jai', 'jei', 'ai', 'ei',
    'ja', 'je', 'uk', 'ük',
    'ok', 'ek', 'ök', 'ak',
    'om', 'em', 'öm', 'od', 'ed', 'öd',
    'ot', 'et', 'öt', 'at',
    'k', 't', 'a', 'e', 's'
  ];

  function byLengthDesc(a, b) {
    return b.length - a.length;
  }

  CASE_SUFFIXES.sort(byLengthDesc);
  LOOSE_SUFFIXES.sort(byLengthDesc);

  function endsWith(word, suffix) {
    return word.slice(-suffix.length) === suffix;
  }

  function stripSuffix(word, suffixes) {
    for (var i = 0; i < suffixes.length; i++) {
      var suffix = suffixes[i];

      if (word.length - suffix.length >= MIN_STEM_LENGTH && endsWith(word, suffix)) {
        return word.slice(0, word.length - suffix.length);
      }
    }

    return word;
  }

  // A stem-final "a"/"e" lengthens before a suffix: alma + t = almát.
  function shortenFinalVowel(word) {
    var last = word.charAt(word.length - 1);

    if (last === 'á') {
      return word.slice(0, -1) + 'a';
    }
    if (last === 'é') {
      return word.slice(0, -1) + 'e';
    }

    return word;
  }

  function stemWord(word, aggressive) {
    if (word.length < MIN_WORD_LENGTH || /\d/.test(word)) {
      return word;
    }

    // -val/-vel assimilates onto the final consonant: bot + val = bottal.
    var assimilated = word.match(/^(.+)(.)\2(?:al|el)$/);

    if (assimilated) {
      var undoubled = assimilated[1] + assimilated[2];

      if (undoubled.length >= MIN_ASSIMILATED_STEM_LENGTH) {
        return shortenFinalVowel(undoubled);
      }
    }

    var stem = stripSuffix(word, CASE_SUFFIXES);

    if (stem !== word) {
      return shortenFinalVowel(stem);
    }

    if (aggressive) {
      stem = stripSuffix(word, LOOSE_SUFFIXES);

      if (stem !== word) {
        return shortenFinalVowel(stem);
      }
    }

    return word;
  }

  function normalizeQuery(query) {
    var words = query.toLowerCase().split(/\s+/);
    var stemmed = [];

    for (var i = 0; i < words.length; i++) {
      stemmed.push(stemWord(words[i], i === words.length - 1));
    }

    return stemmed.join(' ');
  }

  /* Requests
     ========================================================================== */

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || config.root || '/';

  if (root.charAt(root.length - 1) !== '/') {
    root += '/';
  }

  // The shop setting only covers products, articles and pages. Collections are
  // requested on top of it so both the dropdown and the grid can offer them,
  // without changing what the storefront results page itself searches.
  function requestTypes() {
    var types = (config.types || 'product').split(',');

    if (types.indexOf('collection') === -1) {
      types.push('collection');
    }

    return types.join(',');
  }

  function buildUrl(params) {
    return root + 'search/suggest' +
      '?q=' + encodeURIComponent(params.query) +
      '&resources[type]=' + encodeURIComponent(params.types) +
      '&resources[limit]=' + encodeURIComponent(params.limit) +
      '&resources[limit_scope]=' + encodeURIComponent(params.scope) +
      '&section_id=' + encodeURIComponent(params.sectionId);
  }

  // Unwraps the rendered section and reads the result count from whichever
  // counter attribute that section exposes. Caller owns send() and abort().
  function requestSection(params, onSuccess, onFailure) {
    var request = new XMLHttpRequest();

    // A failed request just empties the results, which looks identical to "no
    // matches". Naming the cause keeps that from being invisible.
    function fail(reason) {
      if (window.console && window.console.warn) {
        window.console.warn('live search: ' + reason + ' (' + buildUrl(params) + ')');
      }

      onFailure();
    }

    request.open('GET', buildUrl(params), true);

    request.onload = function () {
      if (request.status < 200 || request.status >= 300) {
        fail('HTTP ' + request.status);
        return;
      }

      var holder = document.createElement('div');
      holder.innerHTML = request.responseText;

      var section = holder.querySelector('#shopify-section-' + params.sectionId);

      if (!section) {
        fail('section "' + params.sectionId + '" missing from the response');
        return;
      }

      var counter = section.querySelector('[' + params.countAttribute + ']');
      var count = counter ? parseInt(counter.getAttribute(params.countAttribute), 10) : 0;

      onSuccess(section.innerHTML, isNaN(count) ? 0 : count);
    };

    request.onerror = function () {
      fail('network error');
    };

    return request;
  }

  var activeRequest = null;

  function fetchResults(query, onSuccess, onFailure) {
    if (activeRequest) {
      activeRequest.abort();
    }

    // Per-type overscan (up to the API max of 10). The section still displays 3
    // and drops collections that have no storefront products, so empty title
    // matches can be skipped without leaving the collections group short.
    var request = requestSection({
      query: query,
      types: requestTypes(),
      limit: config.limit,
      scope: 'each',
      sectionId: config.sectionId,
      countAttribute: 'data-live-search-count'
    }, function (markup, count) {
      activeRequest = null;
      onSuccess(markup, count);
    }, function () {
      activeRequest = null;
      onFailure();
    });

    activeRequest = request;

    request.onabort = function () {
      activeRequest = null;
    };

    request.send();
  }

  /* Rendering
     ========================================================================== */

  // Each input names the container it feeds via its attribute value, so the
  // header popup, the sidebar and the results page stay independent.
  function containers(scope) {
    return document.querySelectorAll(
      scope ? '[data-live-search="' + scope + '"]' : '[data-live-search]'
    );
  }

  function render(scope, markup) {
    var targets = containers(scope);

    for (var i = 0; i < targets.length; i++) {
      targets[i].innerHTML = markup;
      targets[i].classList.add('live-search--active');
    }
  }

  // Called without a scope, this empties every container.
  function clear(scope) {
    var targets = containers(scope);

    for (var i = 0; i < targets.length; i++) {
      targets[i].innerHTML = '';
      targets[i].classList.remove('live-search--active');
    }
  }

  function showMessage(scope, message) {
    render(scope, '<p class="live-search__empty">' + message + '</p>');
  }

  /* Input handling
     ========================================================================== */

  var debounceTimer = null;
  var currentToken = 0;

  // Which query variant last produced results, so submitting the form can reuse
  // it instead of sending text the dropdown never actually searched on.
  var resolved = null;

  function search(scope, rawQuery) {
    var token = ++currentToken;
    var typed = rawQuery.toLowerCase();
    var normalized = normalizeQuery(rawQuery);

    function isStale() {
      return token !== currentToken;
    }

    function finish(effective, markup, count) {
      if (isStale()) {
        return;
      }

      resolved = count > 0 ? { typed: typed, effective: effective } : null;
      render(scope, markup);
    }

    function fail() {
      if (!isStale()) {
        resolved = null;
        clear(scope);
      }
    }

    // The shopper's own words go first. Suffix stripping cannot tell a plural
    // "-ek" from a stem that simply ends that way ("fészek", "gyerek"), and the
    // truncated stem is then what Enter carries to the results page. Stemming
    // is only worth its risk once the literal words have come back empty.
    fetchResults(typed, function (markup, count) {
      if (isStale()) {
        return;
      }

      if (count === 0 && normalized !== typed) {
        fetchResults(normalized, function (retryMarkup, retryCount) {
          finish(normalized, retryMarkup, retryCount);
        }, fail);
        return;
      }

      finish(typed, markup, count);
    }, fail);
  }

  var lastScope = null;

  function handleInput(scope, value) {
    var query = value.trim();

    clearTimeout(debounceTimer);

    // Moving between inputs shouldn't leave the previous one showing old hits.
    if (lastScope && lastScope !== scope) {
      clear(lastScope);
    }

    lastScope = scope;

    if (query.length < MIN_QUERY_LENGTH) {
      currentToken++;
      resolved = null;

      if (activeRequest) {
        activeRequest.abort();
      }

      clear(scope);
      return;
    }

    debounceTimer = setTimeout(function () {
      showMessage(scope, config.searchingLabel);
      search(scope, query);
    }, DEBOUNCE_MS);
  }

  // Delegated so it survives Magnific Popup moving the drawers around the DOM.
  document.addEventListener('input', function (event) {
    var target = event.target;

    if (target && target.hasAttribute && target.hasAttribute('data-live-search-input')) {
      handleInput(target.getAttribute('data-live-search-input'), target.value);
    }
  });

  // Submitting sends whatever the dropdown last searched on, so the results page
  // agrees with the suggestions the shopper just saw. Falls back to the typed
  // text whenever that variant is unknown or stale.
  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (!form || !form.querySelector || !resolved) {
      return;
    }

    var input = form.querySelector('[data-live-search-input]');

    if (!input) {
      return;
    }

    var typed = input.value.trim().toLowerCase();

    if (typed === resolved.typed && resolved.effective !== typed) {
      input.value = resolved.effective;
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.keyCode === 27) {
      clearTimeout(debounceTimer);
      currentToken++;
      resolved = null;
      clear();
    }
  });

  // A results container or a search field is part of the widget, so clicking a
  // suggestion still follows its link. Walks the tree rather than using closest(),
  // which the browsers this theme still styles for do not all have.
  function withinLiveSearch(node) {
    while (node && node.hasAttribute) {
      if (node.hasAttribute('data-live-search') || node.hasAttribute('data-live-search-input')) {
        return true;
      }

      node = node.parentNode;
    }

    return false;
  }

  document.addEventListener('click', function (event) {
    if (withinLiveSearch(event.target)) {
      return;
    }

    // resolved survives on purpose: clicking the submit button arrives here
    // first, and the form still needs the query variant these results came from.
    clearTimeout(debounceTimer);
    currentToken++;
    clear();
  });

  /* Results page grid
     ========================================================================== */

  // The results page ships a loading placeholder instead of storefront hits.
  // swapGrid() replaces it with predictive markup, an empty-state message, or
  // an error if the request fails or times out.
  var GRID_SECTION_ID = 'predictive-grid';
  var GRID_LIMIT = 10;
  var GRID_TIMEOUT_MS = 4000;

  // custom.js re-runs the autoSizer at 1s, 2s and 4s after load because a single
  // pass is not enough for this theme. Injected tiles need the same treatment,
  // measured from the injection instead of from page load.
  var AUTOSIZE_DELAYS = [0, 100, 500, 1000, 2000];

  // data-sizes="auto" is resolved by measuring the rendered element. A width
  // read before the grid has laid out makes RIAS build a src for that wrong
  // width, so the pass has to be repeated until the layout has settled.
  function checkAutoSizes() {
    var autoSizer = window.lazySizes && window.lazySizes.autoSizer;

    if (autoSizer && autoSizer.checkElems) {
      autoSizer.checkElems();
    }
  }

  // Once ScrollReveal puts the sr class on <html>, theme-critical hides every
  // .product-top and .search-page__other-item, and ScrollReveal clears that by
  // setting visibility inline on the elements it registered at page load. These
  // tiles are injected afterwards, so nothing ever registers or clears them.
  // js-sr-loaded is the class its beforeReveal callback adds, which the zoom
  // animation keys off to settle the image back to its natural scale.
  function revealTiles(scope) {
    var tiles = scope.querySelectorAll('.product-top, .search-page__other-item');

    for (var i = 0; i < tiles.length; i++) {
      tiles[i].classList.add('js-sr-loaded');
      tiles[i].style.visibility = 'visible';
    }
  }

  // Lazysizes tiles need unveiling after injection; native src/srcset images do not.
  function unveilImages(scope) {
    var pending = scope.querySelectorAll('.lazyload');

    if (!pending.length) {
      return;
    }

    var loader = window.lazySizes && window.lazySizes.loader;

    if (loader && loader.unveil) {
      for (var i = 0; i < pending.length; i++) {
        loader.unveil(pending[i]);
      }
    }

    for (var d = 0; d < AUTOSIZE_DELAYS.length; d++) {
      setTimeout(checkAutoSizes, AUTOSIZE_DELAYS[d]);
    }
  }

  function swapGrid() {
    var grid = document.querySelector('[data-predictive-grid]');

    if (!grid) {
      return;
    }

    var input = document.querySelector('[data-live-search-input="page"]');
    var query = input ? input.value.trim() : '';
    var settled = false;

    function showStatus(className, html) {
      grid.innerHTML = '<p class="search-page__results-status' + (className ? ' ' + className : '') + '">' + html + '</p>';
    }

    function updateHeading(html) {
      var heading = document.querySelector('[data-search-heading]');

      if (heading && html) {
        heading.innerHTML = html;
      }
    }

    function removePagination() {
      var pagination = document.querySelector('[data-search-pagination]');

      if (pagination && pagination.parentNode) {
        pagination.parentNode.removeChild(pagination);
      }
    }

    // success: injected predictive markup
    // empty: predictive answered but found nothing
    // error: timeout, HTTP failure, or missing section
    function settle(outcome, markup) {
      if (settled) {
        return;
      }

      settled = true;
      grid.classList.remove('is-loading');

      if (outcome === 'success' && markup) {
        grid.innerHTML = markup;
        removePagination();

        var heading = document.querySelector('[data-search-heading]');
        var headingResults = heading && heading.getAttribute('data-heading-results');

        if (headingResults) {
          heading.innerHTML = headingResults;
        }

        revealTiles(grid);
        unveilImages(grid);
        return;
      }

      removePagination();

      if (outcome === 'empty') {
        var noResults = grid.getAttribute('data-no-results-html') || '';

        showStatus('', noResults);
        updateHeading(noResults);
        return;
      }

      showStatus('search-page__results-status--error', grid.getAttribute('data-load-error') || '');
    }

    if (!query) {
      settle('error');
      return;
    }

    setTimeout(function () {
      settle('error');
    }, GRID_TIMEOUT_MS);

    requestSection({
      query: query,
      types: requestTypes(),
      limit: GRID_LIMIT,
      scope: 'each',
      sectionId: GRID_SECTION_ID,
      countAttribute: 'data-predictive-grid-count'
    }, function (markup, count) {
      if (count > 0) {
        settle('success', markup);
      } else {
        settle('empty');
      }
    }, function () {
      settle('error');
    }).send();
  }

  swapGrid();
})();
